// ─── Sending a voice / video message: begin → PUT to R2 → send ───
// The bytes go straight to R2 on a presigned PUT, so the upload shows real progress (XHR)
// and a failure can resume from the step that failed:
//   begin_message_media → { media_id, upload_url, content_type }   (signed for 1 hour)
//   PUT upload_url with exactly that Content-Type and the declared size
//   send_media_message { media_id, caption?, reply_to? } → { message, thread_id }
import { orgApi, orgErr } from './org';

const RESIGN_AFTER_MS = 50 * 60 * 1000; // re-begin rather than PUT on a link about to expire

// PUT `blob` with progress. Rejects with { code: 'upload_failed' | 'network' | 'aborted', status }.
export function putWithProgress(url, blob, contentType, { onProgress, holder } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    if (holder) holder.abort = () => xhr.abort();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => { if (onProgress) onProgress(Math.min(1, e.loaded / (e.total || blob.size || 1))); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject({ code: 'upload_failed', status: xhr.status }));
    xhr.onerror = () => reject({ code: 'network', status: 0 });
    xhr.onabort = () => reject({ code: 'aborted', status: 0 });
    xhr.send(blob);
  });
}

// A job is { blob, content_type, bytes, duration_sec, purpose, exercise, caption, reply_to,
// thread_id | to_email, stage: 'begin' | 'put' | 'send' | 'done', media_id?, upload_url?,
// put_type?, began_at? }. Runs from `job.stage`, mutating the job so a retry resumes where
// it stopped. Resolves with the send_media_message response (message null when the server
// already had it); rejects with an orgErr-shaped { status, code, ... }.
export async function runMediaJob(job, { onProgress, onStage, holder } = {}) {
  const stage = (s) => { job.stage = s; if (onStage) onStage(s); };
  if (job.stage === 'put' && (!job.upload_url || Date.now() - (job.began_at || 0) > RESIGN_AFTER_MS)) stage('begin');
  if (!job.stage || job.stage === 'begin') {
    stage('begin');
    let r;
    try {
      r = await orgApi('begin_message_media', {
        ...(job.thread_id ? { thread_id: job.thread_id } : { to_email: job.to_email }),
        content_type: job.content_type, bytes: job.bytes,
        ...(job.duration_sec ? { duration_sec: job.duration_sec } : {}),
        purpose: job.purpose || 'message', ...(job.exercise ? { exercise: job.exercise } : {}),
      });
    } catch (e) { throw orgErr(e); }
    job.media_id = r.media_id; job.upload_url = r.upload_url; job.put_type = r.content_type || job.content_type;
    job.began_at = Date.now();
    if (r.thread_id) job.thread_id = r.thread_id;
    stage('put');
  }
  if (job.stage === 'put') {
    if (onProgress) onProgress(0);
    await putWithProgress(job.upload_url, job.blob, job.put_type, { onProgress, holder });
    if (onProgress) onProgress(1);
    stage('send');
  }
  if (job.stage === 'send') {
    try {
      const r = await orgApi('send_media_message', { media_id: job.media_id, ...(job.caption ? { caption: job.caption } : {}), ...(job.reply_to ? { reply_to: job.reply_to } : {}) });
      stage('done');
      return r;
    } catch (e) {
      const er = orgErr(e);
      if (er.code === 'already_sent') { stage('done'); return { message: null, thread_id: job.thread_id }; }
      if (er.code === 'upload_missing') stage('put');          // the PUT never landed: upload again
      else if (er.code === 'upload_mismatch') stage('begin');  // wrong object: start over
      else if (er.code === 'invalid_reply_to') job.reply_to = null; // quoted message gone: send without it
      throw er;
    }
  }
  return { message: null, thread_id: job.thread_id };
}
